/**
 * Dokument smlouvy jako A4 (krok UXS1).
 *
 * R.B. 1. 10.: „smlouvy nemají editor" — v pravem panelu byl HOLY text se
 * slugem sablony a cisly odstavcu v zavorkach. Mockup
 * `docs/platform/ui/_designs/smlouvy/reos-smlouvy-aplikace-mockup.html` ma
 * vpravo DOKUMENT: A4, nadpis, clanky, cislovane body, hodnoty zvyraznene
 * podle puvodu.
 *
 * Sazba JE JEDNA, ale od kroku PRF5 nezije tady: `CONTRACT_DOC_CSS` je
 * u rendereru v `@reos/documents` (`internal/contracts/doc-css.ts`) a admin
 * i verejny web ji dostanou V DTO (`documentCss`). Duvod je nalez PRF4 —
 * klientsky nahled smlouvy zadnou sazbu nemel a slepoval opakovane polozky,
 * protoze druhy frontend na konstantu administrace nedosahne. HTML dokumentu
 * sklada server (`renderContract`) — jeden renderer pro editor, tisk
 * i ulozenou verzi, a ted i jeden stylopis.
 */

import { formatCzechDate } from '@reos/i18n';

/**
 * Cele okno tisku: `GET /v1/contracts/:id/print` vraci HTML smlouvy
 * (s pruhem KONCEPT u neuplne smlouvy) A JEJI SAZBU (`documentCss`, krok
 * PRF5); do UXS1 tu sazba nebyla vubec a tisk vypadal jako holy text. Nazev dokumentu je zaroven vychozi
 * nazev souboru, kdyz si clovek v dialogu vybere „Uložit jako PDF".
 */
export function printDocumentHtml(contractHtml: string, documentTitle: string, documentCss: string): string {
  const safeTitle = documentTitle.replace(/[<&>]/g, '');
  return [
    '<!DOCTYPE html>',
    '<html lang="cs"><head><meta charset="utf-8">',
    `<title>${safeTitle}</title>`,
    `<style>${documentCss}</style>`,
    '</head><body>',
    `<div class="reos-doc">${contractHtml}</div>`,
    '</body></html>',
  ].join('');
}

/** Nazev souboru pro tisk/PDF bez diakritiky a bez mezer (mockup `pdfName`). */
export function documentFileName(title: string): string {
  const ascii = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return ascii === '' ? 'smlouva' : ascii;
}

/**
 * Z upraveneho odstavce v dokumentu A4 zpatky TEXT SABLONY.
 *
 * „Upravit text smlouvy" v mockupu neni samostatna obrazovka s holym textem:
 * clovek prepisuje vetu primo v dokumentu a POLE zustavaji nedelitelne znacky.
 * Kazdy token proto nese v HTML `data-t` (`contract.ts`) a tahle funkce ho
 * vraci do podoby `{{token}}` — ulozena rucni uprava tak zustava ZIVA (pole se
 * dal dosazuji), presne jak to engine T1 umi.
 */
export function blockTemplateTextFrom(node: Node): string {
  let out = '';
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3 /* text */) {
      out += child.textContent ?? '';
      return;
    }
    if (child.nodeType !== 1 /* element */) return;
    const el = child as Element;
    /*
     * Krok UXD1 bod 7 (R.B. 12. 10. 21:40: maklerka chce mit tucne cenu).
     * Tlacitko B v rezimu „Upravit text" dela `document.execCommand('bold')`,
     * takze v odstavci vznikne `<b>` nebo `<strong>`. Do UXD1 se tady zahodilo
     * a ulozil se HOLY text — tucne tedy po ulozeni (a v PDF) zmizelo.
     *
     * Sablona nese tucne jako `**…**`: tentyz tvar, jaky umi renderer smlouvy
     * (`fillText` v @reos/documents), takze zustane v nahledu, v ulozene verzi,
     * v tisku, v PDF i na strance klienta — jeden renderer pro vsechny.
     * Obsah se prochazi dal, aby `{{tokeny}}` uvnitr tucneho zustaly ZIVE
     * (`**{{najem.cena}}**`).
     */
    if (el.tagName === 'B' || el.tagName === 'STRONG') {
      const inner = blockTemplateTextFrom(el);
      out += inner.trim() === '' ? inner : `**${inner}**`;
      return;
    }
    // Krok SML3: `<del class="reos-doc__del">` je TEXT MUSTRU, ktery se
    // v rezimu „Zobrazit změny" sazi PRESKRTNUTY kvuli porovnani — ve smlouve
    // uz NESTOJI. Kdyby se ukladal, kazde ulozeni v tom rezimu by do odstavce
    // vratilo i text, ktery z nej clovek prave odstranil.
    if (el.tagName === 'DEL' && el.classList.contains('reos-doc__del')) return;
    const token = el.getAttribute('data-t');
    if (token !== null && token !== '') {
      out += `{{${token}}}`;
      return;
    }
    if (el.tagName === 'BR') {
      out += '\n';
      return;
    }
    out += blockTemplateTextFrom(el);
  });
  return out;
}

/**
 * STAV TLACITKA „B" u radku formulare (krok UXD3 bod 4).
 *
 * Tentyz dvoutermovy vypocet dela SERVER v `boldForField` (@reos/documents,
 * `contract.ts`) — tam je sazba, tady jen poloha prepinace. Druha kopie je
 * tu vedome: `@reos/web-admin` na `@reos/documents` nezavisi
 * (pravidlo UI jde pres DTO, ne pres import) a posilat efektivni mapu pro
 * vsechna pole by do kazde odpovedi pridalo seznam, ktery se da spocitat
 * z dvou poli, ktere uz v DTO jsou. Test `contract-doc.spec.ts` drzi obe
 * strany na stejne tabulce pripadu.
 */
export function boldForFieldInContract(
  fieldKey: string,
  contract: {
    readonly boldFilledFields?: boolean | undefined;
    readonly boldFieldOverrides?: Readonly<Record<string, boolean>> | undefined;
  },
): boolean {
  const exception = contract.boldFieldOverrides?.[fieldKey];
  return exception ?? contract.boldFilledFields === true;
}

/**
 * Klic pole „predmet najmu". Formular ho nema jako radek seznamu poli (ma
 * vlastni oddil s vyberem), engine ho ale hlasi jako nevyplnene pole, kdyz je
 * ve smlouve `{{predmet:N}}` a predmet zadany neni.
 */
export const SUBJECT_TYPE_FIELD_KEY = 'najem.predmet-typ';

/**
 * HODNOTA POLE TAK, JAK JI VYSAZI DOKUMENT (krok NOC4 bod 4, nalez N23).
 *
 * Zivy nahled v zalozce „Vyplnit" si novou hodnotu vepisuje do dokumentu sam,
 * nez prijde odpoved serveru (`applyLocalEdits` v `components/contracts.tsx`)
 * — a vepisoval ji SUROVOU, takze datum zustalo v ISO (`2026-10-14`), zatimco
 * „Náhled pro klienta" i PDF mely „14. 10. 2026". Zapisy se navic po odpovedi
 * serveru nemazou, takze ISO datum v dokumentu zustalo az do dalsiho nacteni.
 *
 * Prevod je TENTYZ, jaky dela render smlouvy na serveru (`czechDateText`
 * v `@reos/documents`): oba ctou `formatCzechDate` z `@reos/i18n`, protoze
 * admin kontext `documents` importovat nesmi (CLAUDE.md pravidlo 4).
 * Co datum neni, projde beze zmeny — formatovac nikdy nezahodi hodnotu,
 * kterou nezna.
 */
export function documentValueText(value: string): string {
  const czech = formatCzechDate(value.trim());
  return czech === '' ? value : czech;
}


/**
 * `data-testid` ovladaciho prvku pole ve formulari editoru.
 *
 * Tim se zamyka obe strany vazby „klik v dokumentu zameri pole ve formulari":
 * dokument nese `data-f` (klic slovniku) a u pole strany i `data-i` (poradi
 * strany), formular ma stejne slozeny `data-testid`.
 */
export function fieldControlTestId(fieldKey: string, index: string | null): string {
  // Predmet najmu ma vlastni oddil formulare („Předmět smlouvy"), ne radek
  // v seznamu poli — klik na `[chybí]` u skloneneho predmetu v dokumentu musi
  // zamerit ten vyber (UXS2 bod 4).
  if (fieldKey === SUBJECT_TYPE_FIELD_KEY) return 'contract-subject-select';
  const party = /^(.+)\[\]\.(.+)$/.exec(fieldKey);
  if (party !== null) {
    // Sablona v1 ma pro stranu PEVNE MISTO bez poradi; je to prvni strana
    // seznamu role, jak ji dosazuje engine (UXS2 bod 1). Bez teto zalohy
    // koncil klik na jmeno pronajimatele na `BODY` — nalez R.B. 1. 10.
    return `contract-party-${party[1] ?? ''}-${index !== null && index !== '' ? index : '0'}-${party[2] ?? ''}`;
  }
  return `contract-field-${fieldKey}`;
}

/**
 * Nazev oddilu formulare podle clanku: `Čl. I` zustava, ale technicky nazev
 * ze souboru (`najemni-smlouva-mustr`) ne — v levem panelu by byl stejne
 * matouci jako byl v dokumentu.
 */
export function articleSectionTitle(title: string): string {
  const trimmed = title.trim();
  return /^[\p{Ll}\p{N}]+(?:[-_][\p{Ll}\p{N}]+)+$/u.test(trimmed) ? '' : trimmed;
}

/**
 * Bloky, ktere NEJDE upravovat primo v dokumentu: vznikly rozmnozenim
 * opakovaneho bloku (`{{opakuj:pronajimatel[]}}`). Rucni uprava by zmrazila
 * pocet stran — strany patri do formulare vlevo, ne do textu.
 */
export function expandedBlockIds(
  articles: readonly {
    readonly blocks: readonly { readonly id: string; readonly text: string }[];
  }[],
): ReadonlySet<string> {
  const out = new Set<string>();
  for (const article of articles) {
    for (const block of article.blocks) {
      if (block.text.includes('{{opakuj:')) out.add(block.id);
    }
  }
  return out;
}
